import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LoadingController, ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { AttributesService } from 'src/app/processes/attributes.service';
import { ProcessesService } from 'src/app/processes/processes.service';
import { AttributeItem } from 'src/app/shared/interfaces/attribute-item';
import { QuestionAttribute, QuestionConfig } from 'src/app/shared/interfaces/process-config.interface';
import { AttributeTypeEnum } from 'src/app/shared/modals/filters/models/filter-models';

@Component({
  selector: 'app-question-group-attributes',
  templateUrl: './question-attributes.component.html',
  styleUrls: ['./question-attributes.component.scss'],
})
export class QuestionAttributesComponent implements OnInit, OnDestroy {

  AttributeTypeEnum = AttributeTypeEnum;
  unsubscribeAll: Subject<any>;

  @Input() question: QuestionConfig;

  public formGroup: UntypedFormGroup;
  public attributes: AttributeItem[] = [];
  public attributeTypes: AttributeTypeEnum[] = [];
  public selectedAttributes: AttributeItem[] = [];

  constructor(
    private _attributesService: AttributesService,
    private _processService: ProcessesService,
    private _formBuilder: UntypedFormBuilder,
    private _loadingController: LoadingController,
    public _modalController: ModalController,
  ) {
    this.unsubscribeAll = new Subject();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  ngOnInit() {
    this.attributeTypes = AttributeTypeEnum.allValues();
    this.selectedAttributes = this.question.attributes
      ? this.question.attributes.map(a => <AttributeItem>{ id: a.attributeId, type: a.attribute.type, name: a.attribute.name })
      : [];
    this._createFormGroup();
    this.initSubscriptions();
  }

  private _createFormGroup() {
    this.formGroup = this._formBuilder.group({
      attributeType: [this.attributeTypes[0].type, [Validators.required]],
      attributeId: ['', [Validators.required]],
    });
  }

  private initSubscriptions(): void {
    this.formGroup.controls.attributeType
      .valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(1000),
        takeUntil(this.unsubscribeAll),
        startWith(this.formGroup.controls.attributeType.value))
      .subscribe(async (t) => await this.getAttributes(t));
  }

  private async getAttributes(attributeType: number) {
    let result: AttributeItem[] = [];
    const loading = await this._loadingController.create();
    await loading.present();
    try {
      const response = await this._attributesService.searchAttributes(attributeType, false, true).toPromise();
      result = response.data;
      this.formGroup.get('attributeId').reset('');
      loading.dismiss();
    } catch (ex) {
      loading.dismiss();
    }
    this.attributes = result;
  }

  public addAttribute(): void {
    const id = this.formGroup.controls.attributeId.value;
    const attr = this.attributes.find(x => x.id === id);
    if (attr && !this.selectedAttributes.some(a => a.id === attr.id)) {
      this.selectedAttributes.push(attr);
    }
  }

  public removeAttribute(index: number): void {
    this.selectedAttributes.splice(index, 1);
  }

  public async save(): Promise<void> {
    this.question.attributes = this.selectedAttributes
      ? this.selectedAttributes.map(
        x => ({ questionId: this.question.id, attributeId: x.id, attribute: x } as QuestionAttribute)
      )
      : [];
    this.close();
  }

  public close(): void {
    this._modalController.dismiss();
  }

  public GetDateToString(date) {
    return date.toString();
  }
}

